import React, { useState, useCallback, forwardRef } from 'react'
import { Button, Card, Stack, Text, Spinner, Flex, Badge, Heading, Box } from '@sanity/ui'
import { ObjectInputProps, useFormValue, useClient } from 'sanity'
import { getFrontendURL } from '../utils/environment'

interface ScoreResult {
  key: string
  fitmentTypeKey: string
  scores: {
    wheelWidthScore: number
    tireWidthScore: number
    tireCategoryScore: number
    diameterScore: number
    rawScore: number
    normalizedScore: number
  }
}

interface CalculationResponse {
  success: boolean
  dryRun?: boolean
  message?: string
  summary?: {
    totalListings: number
    calculated: number
    skipped: number
  }
  results?: ScoreResult[]
}

const PerformanceScoreButton = forwardRef<HTMLDivElement, ObjectInputProps>(
  (props, ref) => {
    // Get document ID using Sanity v4 hook
    const rawId = useFormValue(['_id']) as string | undefined
    const documentId = rawId?.replace('drafts.', '')
    
    // Get current dataset from Sanity client
    // In Sanity Studio v4, useClient() returns a client configured for the current workspace
    const client = useClient({ apiVersion: '2024-01-01' })
    // Access dataset from client config - try multiple possible ways
    const currentDataset = 
      (client as any).config?.()?.dataset || 
      (client as any).clientConfig?.dataset || 
      'preview'

    const [isLoading, setIsLoading] = useState(false)
    const [response, setResponse] = useState<CalculationResponse | null>(null)
    const [lastAction, setLastAction] = useState<'preview' | 'save' | null>(null)

    const handleCalculate = useCallback(async (dryRun: boolean = false) => {
      if (!documentId) {
        setResponse({
          success: false,
          message: 'Document must be saved first',
        })
        return
      }

      setIsLoading(true)
      setResponse(null)
      setLastAction(dryRun ? 'preview' : 'save')

      try {
        const apiUrl = `${getFrontendURL()}/api/scoring/calculate-performance-scores`
        const headers: Record<string, string> = { 'Content-Type': 'application/json' }

        // Add API key if configured
        const apiKey = process.env.SANITY_STUDIO_INTERNAL_API_KEY
        if (apiKey) {
          headers['x-internal-api-key'] = apiKey
        }

        const res = await fetch(apiUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            buyerGuideId: documentId,
            force: true,
            dryRun,
            dataset: currentDataset, // Pass current dataset to API
          }),
        })

        const data: CalculationResponse = await res.json()
        setResponse(data)
      } catch (error: any) {
        if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
          setResponse({
            success: false,
            message: `Cannot connect to server. Make sure the frontend dev server is running at ${getFrontendURL()}`,
          })
        } else {
          setResponse({
            success: false,
            message: error.message || 'Network error',
          })
        }
      } finally {
        setIsLoading(false)
      }
    }, [documentId])

    const getScoreTone = (score: number): 'positive' | 'caution' | 'critical' => {
      if (score >= 7) return 'positive'
      if (score >= 4) return 'caution'
      return 'critical'
    }

    // Round score to nearest 0.5 increment (matches what gets saved to Scoring tab)
    const roundToHalf = (score: number): number => Math.round(score * 2) / 2

    return (
      <Stack gap={4} ref={ref}>
        <Card padding={4} radius={2} shadow={1}>
          <Stack gap={3}>
            <Heading size={1}>Performance Score Calculator</Heading>
            <Text size={1} muted>
              Calculate performance scores for all fitment listings based on wheel width, tire width,
              tire category, and wheel diameter. Scores are normalized so the best fitment gets 10.
            </Text>

            <Flex gap={2} wrap="wrap">
              <Button
                text={isLoading && lastAction === 'preview' ? 'Previewing...' : 'Preview Scores'}
                tone="default"
                onClick={() => handleCalculate(true)}
                disabled={isLoading || !documentId}
              />
              <Button
                text={isLoading && lastAction === 'save' ? 'Calculating...' : 'Calculate & Save'}
                tone="primary"
                onClick={() => handleCalculate(false)}
                disabled={isLoading || !documentId}
              />
            </Flex>

            {!documentId && (
              <Text size={1} muted>
                Save the document first to calculate scores.
              </Text>
            )}
          </Stack>
        </Card>

        {isLoading && (
          <Card padding={4} radius={2}>
            <Flex align="center" justify="center" gap={3}>
              <Spinner />
              <Text size={1}>
                {lastAction === 'preview'
                  ? 'Previewing performance scores...'
                  : 'Calculating and saving performance scores...'}
              </Text>
            </Flex>
          </Card>
        )}

        {response && !response.success && (
          <Card padding={4} radius={2} tone="critical" shadow={1}>
            <Stack gap={2}>
              <Text size={1} weight="semibold">Error</Text>
              <Text size={1}>{response.message}</Text>
            </Stack>
          </Card>
        )}

        {response && response.success && response.results && (
          <Card padding={4} radius={2} shadow={1}>
            <Stack gap={4}>
              <Flex align="center" justify="space-between" wrap="wrap" gap={2}>
                <Heading size={1}>
                  {lastAction === 'preview' ? 'Preview Results' : 'Saved Results'}
                </Heading>
                <Flex gap={2}>
                  <Badge tone={lastAction === 'save' ? 'positive' : 'caution'}>
                    {lastAction === 'save' ? 'Saved' : 'Preview Only'}
                  </Badge>
                  <Badge tone="default">
                    {response.summary?.totalListings || response.results.length} listings
                  </Badge>
                </Flex>
              </Flex>

              {/* Summary Stats */}
              <Card padding={3} radius={2} tone="default">
                <Flex gap={4} wrap="wrap">
                  <Text size={1}>
                    <strong>Calculated:</strong> {response.summary?.calculated || response.results.length}
                  </Text>
                  {(response.summary?.skipped || 0) > 0 && (
                    <Text size={1} muted>
                      <strong>Skipped:</strong> {response.summary?.skipped}
                    </Text>
                  )}
                </Flex>
              </Card>

              {/* Results Table */}
              <Box
                style={{
                  maxHeight: '400px',
                  overflow: 'auto',
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '13px',
                  }}
                >
                  <thead>
                    <tr style={{ position: 'sticky', top: 0, backgroundColor: 'var(--card-bg-color)' }}>
                      <th style={{ padding: '10px 8px', borderBottom: '2px solid var(--card-border-color)', textAlign: 'left' }}>
                        #
                      </th>
                      <th style={{ padding: '10px 8px', borderBottom: '2px solid var(--card-border-color)', textAlign: 'center', width: '70px' }}>
                        Wheel W
                      </th>
                      <th style={{ padding: '10px 8px', borderBottom: '2px solid var(--card-border-color)', textAlign: 'center', width: '70px' }}>
                        Tire W
                      </th>
                      <th style={{ padding: '10px 8px', borderBottom: '2px solid var(--card-border-color)', textAlign: 'center', width: '70px' }}>
                        Tire Cat
                      </th>
                      <th style={{ padding: '10px 8px', borderBottom: '2px solid var(--card-border-color)', textAlign: 'center', width: '70px' }}>
                        Diameter
                      </th>
                      <th style={{ padding: '10px 8px', borderBottom: '2px solid var(--card-border-color)', textAlign: 'center', width: '70px' }}>
                        Raw
                      </th>
                      <th style={{ padding: '10px 8px', borderBottom: '2px solid var(--card-border-color)', textAlign: 'center', width: '80px' }}>
                        Final
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {response.results.map((r, idx) => (
                      <tr key={r.key}>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--card-border-color)' }}>
                          {idx + 1}
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--card-border-color)', textAlign: 'center' }}>
                          {r.scores.wheelWidthScore.toFixed(1)}
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--card-border-color)', textAlign: 'center' }}>
                          {r.scores.tireWidthScore.toFixed(1)}
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--card-border-color)', textAlign: 'center' }}>
                          {r.scores.tireCategoryScore.toFixed(1)}
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--card-border-color)', textAlign: 'center' }}>
                          {r.scores.diameterScore.toFixed(1)}
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--card-border-color)', textAlign: 'center' }}>
                          {r.scores.rawScore.toFixed(2)}
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--card-border-color)', textAlign: 'center' }}>
                          <Badge tone={getScoreTone(r.scores.normalizedScore)}>
                            {roundToHalf(r.scores.normalizedScore)}/10
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>

              {/* Score Legend */}
              <Flex gap={3} wrap="wrap">
                <Text size={0} muted>
                  Score Legend:
                </Text>
                <Flex gap={2} align="center">
                  <Badge tone="positive">7-10</Badge>
                  <Text size={0} muted>Excellent</Text>
                </Flex>
                <Flex gap={2} align="center">
                  <Badge tone="caution">4-6</Badge>
                  <Text size={0} muted>Average</Text>
                </Flex>
                <Flex gap={2} align="center">
                  <Badge tone="critical">1-3</Badge>
                  <Text size={0} muted>Limited</Text>
                </Flex>
              </Flex>

              {lastAction === 'save' && (
                <Card padding={3} radius={2} tone="positive">
                  <Text size={1}>
                    Changes have been saved to a draft. Review and publish when ready.
                  </Text>
                </Card>
              )}
            </Stack>
          </Card>
        )}
      </Stack>
    )
  }
)

PerformanceScoreButton.displayName = 'PerformanceScoreButton'

export default PerformanceScoreButton
